/**
 * AD-2.1 PR4 — useReflowOffer.
 *
 * Fetches the mid-week reflow OFFER for the athlete's current week from
 * `GET /api/plans/reflow-offer` and exposes the lifecycle actions (accept /
 * dismiss / mark-shown). The server is authoritative for everything that
 * matters: it gates on the `midweek_reflow_v1` flag (offer is null when off),
 * decides whether to surface, and re-derives + re-validates on accept. This hook
 * is presentation glue only — it never decides safety.
 *
 * When the flag is off OR the server-only reflow store is unconfigured, the
 * endpoint returns `offer: null` (or 503), so this hook yields no offer and the
 * card never renders — flag-off is inert on the client too.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthFetch } from './useAuthFetch';

export interface ReflowDiffDay {
  day_label?: string | null;
  changedFields?: string[];
  after?: Record<string, unknown> | null;
}

export interface ReflowOffer {
  proposalHash: string;
  changed: boolean;
  /** 'card' = actionable; 'absorbed_note' = one-time non-actionable note. */
  kind: 'card' | 'absorbed_note';
  summary: string;
  diff: { days?: ReflowDiffDay[] } | null;
  reasonCodes: string[];
  load: { recoveredLoad: number; absorbedShortfallLoad: number; easedFutureLoad: number };
  changedDayLabels: string[];
}

export interface UseReflowOfferResult {
  offer: ReflowOffer | null;
  /** CG-3 — true when a valid proposal is held pending guardian confirmation
   *  (U16 gate). The offer is null while held; surfaces show a waiting note. */
  held: boolean;
  loading: boolean;
  error: Error | null;
  /** Accept the offer → server re-derives + applies via the hardened RPC. */
  accept: () => Promise<void>;
  /** Dismiss the offer (suppresses all surfaces for this miss). */
  dismiss: () => Promise<void>;
  /** Record that a surface showed this offer (idempotent per surface). */
  markShown: (surface: 'today' | 'chat') => Promise<void>;
  refetch: () => Promise<void>;
}

export function useReflowOffer({ seasonId }: { seasonId: string | null }): UseReflowOfferResult {
  const fetchAuthed = useAuthFetch();
  const [offer, setOffer] = useState<ReflowOffer | null>(null);
  const [held, setHeld] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!seasonId) {
      setOffer(null);
      setHeld(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchAuthed(`/api/plans/reflow-offer?seasonId=${encodeURIComponent(seasonId)}`);
      if (!res.ok) {
        // 503 (store unavailable) / any non-2xx → no offer (feature dormant).
        setOffer(null);
        setHeld(false);
        setError(null);
        return;
      }
      const body = (await res.json()) as { offer: ReflowOffer | null; held?: boolean };
      setOffer(body && body.offer ? body.offer : null);
      setHeld(body ? body.held === true : false);
      setError(null);
    } catch (err) {
      setOffer(null);
      setHeld(false);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [seasonId, fetchAuthed]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const accept = useCallback(async (): Promise<void> => {
    if (!offer || !seasonId) return;
    const res = await fetchAuthed('/api/plans/reflow-apply', {
      method: 'POST',
      body: JSON.stringify({ seasonId, proposalHash: offer.proposalHash }),
    });
    // On success OR a 409 stale, the offer is no longer actionable — clear + refetch.
    setOffer(null);
    if (!res.ok && res.status !== 409) {
      throw new Error(`reflow-apply responded ${res.status}`);
    }
    if (res.ok) {
      // CG-3 — a 200 may be a guardian hold rather than an apply.
      const body = (await res.json().catch(() => null)) as { heldForGuardian?: boolean } | null;
      if (body && body.heldForGuardian === true) setHeld(true);
    }
    void refetch();
  }, [offer, seasonId, fetchAuthed, refetch]);

  const dismiss = useCallback(async (): Promise<void> => {
    if (!offer) return;
    setOffer(null);
    await fetchAuthed('/api/plans/reflow-dismiss', {
      method: 'POST',
      body: JSON.stringify({ proposalHash: offer.proposalHash }),
    }).catch(() => {});
  }, [offer, fetchAuthed]);

  const markShown = useCallback(
    async (surface: 'today' | 'chat'): Promise<void> => {
      if (!offer) return;
      await fetchAuthed('/api/plans/reflow-shown', {
        method: 'POST',
        body: JSON.stringify({ proposalHash: offer.proposalHash, surface }),
      }).catch(() => {});
    },
    [offer, fetchAuthed],
  );

  return { offer, held, loading, error, accept, dismiss, markShown, refetch };
}
